import { useCursor } from "@/contexts/cursor"
import { useCursorCapture } from "@/hooks/cursor/useCursorCapture"
import { useNodeSelection } from "../../hooks/project/use-node-selection"
import { useAuth } from "@/providers/auth"
import type { ProjectNodeData } from "@/types/project"
import { mutators } from "@oblique/shared/zero"
import { useZero } from "@rocicorp/zero/react"
import type { Connection, Node, OnConnectStart } from "@xyflow/react"
import { useReactFlow, useStoreApi } from "@xyflow/react"
import type { MouseEvent, MutableRefObject } from "react"
import { useCallback } from "react"
import { useParams } from "react-router"

// ===== Types =====

type NodeEventHandler = (event: MouseEvent, node: Node<ProjectNodeData>) => void
type NodesEventHandler = (
	event: MouseEvent,
	nodes: Node<ProjectNodeData>[],
) => void

// ===== Helper Functions =====

function updateMousePosition(
	event: MouseEvent,
	ref: MutableRefObject<{ x: number; y: number }>,
) {
	if (event.clientX !== undefined && event.clientY !== undefined) {
		ref.current = { x: event.clientX, y: event.clientY }
	}
}

function mapNodesToDragData(nodes: Node<ProjectNodeData>[]) {
	return nodes.map((n) => ({ id: n.id, position: n.position }))
}

/**
 * Coordinates workspace interactions: selection, cursor sync, and event handlers.
 */
export function useWorkspaceInteractions() {
	// ===== Setup & Dependencies =====
	const params = useParams<{ projectId: string }>()
	const reactFlowInstance = useReactFlow()
	const auth = useAuth()
	const cursor = useCursor()
	const storeApi = useStoreApi()

	const z = useZero()

	const projectId = params?.projectId ?? ""
	const screenToFlowPosition =
		reactFlowInstance?.screenToFlowPosition ??
		((pos: { x: number; y: number }) => pos)
	const user = auth?.user ?? { id: "" }
	const connectionId = cursor?.connectionId ?? null
	const connectionState = cursor?.connectionState ?? ""
	const updateCursor = cursor?.updateCursor ?? (() => {})

	const getSelectedNodeIds = useCallback(() => {
		try {
			const nodes = storeApi?.getState?.().nodes ?? []
			return nodes.filter((n) => n.selected).map((n) => n.id)
		} catch {
			return []
		}
	}, [storeApi])

	// ===== Core Hooks =====

	const cursorCapture = useCursorCapture({
		connectionId,
		userId: user.id ?? "",
		projectId,
		onCursorUpdate: updateCursor,
		enabled: connectionState === "connected" || connectionState === "fallback",
	})

	const connect = useCallback(
		(connection: {
			source: string
			target: string
			sourceHandle?: string | null
			targetHandle?: string | null
		}) => {
			if (!projectId) return
			if (!connection.sourceHandle || !connection.targetHandle) return

			z.mutate(
				mutators.edges.add({
					project_id: projectId,
					source_node_id: connection.source,
					source_handle: connection.sourceHandle,
					target_node_id: connection.target,
					target_handle: connection.targetHandle,
				}),
			)
		},
		[projectId, z.mutate],
	)

	const selection = useNodeSelection({
		screenToFlowPosition,
		getSelectedNodeIds,
		setSelecting: cursorCapture.setSelecting,
		updateSelectionBox: cursorCapture.updateSelectionBox,
	})

	// ===== Event Handlers =====

	const onNodeDragStart = useCallback<NodeEventHandler>(
		(_, node) => {
			cursorCapture.setDragging([{ id: node.id, position: node.position }])
		},
		[cursorCapture.setDragging],
	)

	const onNodeDrag = useCallback<NodeEventHandler>(
		(event, node) => {
			updateMousePosition(event, cursorCapture.lastMousePositionRef)
			cursorCapture.updateDragPositions([
				{ id: node.id, position: node.position },
			])
		},
		[cursorCapture.updateDragPositions, cursorCapture.lastMousePositionRef],
	)

	const onNodeDragStop = useCallback<NodeEventHandler>(() => {
		// Position updates are now handled by onNodesChange in useZeroFlowState
		cursorCapture.setDragging(null)
	}, [cursorCapture.setDragging])

	const onSelectionDragStart = useCallback<NodesEventHandler>(
		(_, nodes) => {
			cursorCapture.setDragging(mapNodesToDragData(nodes))
		},
		[cursorCapture.setDragging],
	)

	const onSelectionDrag = useCallback<NodesEventHandler>(
		(event, nodes) => {
			updateMousePosition(event, cursorCapture.lastMousePositionRef)
			cursorCapture.updateDragPositions(mapNodesToDragData(nodes))
		},
		[cursorCapture.updateDragPositions, cursorCapture.lastMousePositionRef],
	)

	const onSelectionDragStop = useCallback<NodesEventHandler>(() => {
		// Position updates are now handled by onNodesChange in useZeroFlowState
		cursorCapture.setDragging(null)
	}, [cursorCapture.setDragging])

	// TODO: what is this for?
	// const onNodeClick = useCallback<NodeEventHandler>(
	// 	(_, node) => {
	// 		trackNodeCreated(node.id)
	// 	},
	// 	[trackNodeCreated],
	// )

	const onConnect = useCallback(
		(connection: Connection) => {
			connect({
				source: connection.source ?? "",
				target: connection.target ?? "",
				sourceHandle: connection.sourceHandle ?? null,
				targetHandle: connection.targetHandle ?? null,
			})
		},
		[connect],
	)

	const onConnectStart: OnConnectStart = useCallback(
		(_, params) => {
			if (params.nodeId && params.handleType) {
				cursorCapture.setConnecting({
					nodeId: params.nodeId,
					handleId: params.handleId,
					handleType: params.handleType,
				})
			}
		},
		[cursorCapture.setConnecting],
	)

	const onConnectEnd = useCallback(() => {
		cursorCapture.setConnecting(null)
	}, [cursorCapture.setConnecting])

	const onNodesDelete = useCallback(
		(_deletedNodes: Node<ProjectNodeData>[]) => {
			// Node deletions are now handled by onNodesChange in useZeroFlowState
		},
		[],
	)

	const handlers = {
		onNodeDragStart,
		onNodeDrag,
		onNodeDragStop,
		onSelectionDragStart,
		onSelectionDrag,
		onSelectionDragStop,
		onConnect,
		onConnectStart,
		onConnectEnd,
		onNodesDelete,
	}

	return {
		selection,
		cursorCapture,
		handlers,
	}
}
